import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import StaffShell from "@/components/layout/StaffShell";
import { staffApi, formatApiError, formatIDR } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { hasPermission } from "@/lib/permissions";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Tag, ChevronDown, ChevronRight } from "lucide-react";

const TYPE_LABELS = {
  auto_all: "Auto apply to all customers",
  auto_members: "Auto apply to all members",
  code: "Code required manually",
};
const WEEK = [
  { key: "mon", label: "Monday" }, { key: "tue", label: "Tuesday" }, { key: "wed", label: "Wednesday" },
  { key: "thu", label: "Thursday" }, { key: "fri", label: "Friday" }, { key: "sat", label: "Saturday" },
  { key: "sun", label: "Sunday" },
];
const STEP_TITLES = [
  "Name & Type", "Booking Date Range", "Applicable Time", "Applicable Days", "Special Items", "Pricing Information",
];

const empty = {
  name: "", description: "", special_type: "code", promo_code: "", internal_only: false,
  starts_on: "", ends_on: "",
  time_mode: "flexible", time_start: "10:00", time_end: "16:00",
  day_mode: "anyday", days: [],
  items_mode: "all", item_ids: [],
  pricing_mode: "discount", discount_type: "percent", discount_value: 10,
  price_ranges: [], addon_kind: "product", addon_ref_id: "",
  active: true,
};

function ymdToDmy(ymd) {
  if (!ymd) return "";
  const [y, m, d] = ymd.split("-");
  return `${d}/${m}/${y.slice(2)}`;
}
function dmyToYmd(s) {
  const m = (s || "").trim().match(/^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{2,4})$/);
  if (!m) return null;
  const d = parseInt(m[1], 10), mo = parseInt(m[2], 10);
  let y = m[3]; y = y.length === 2 ? 2000 + parseInt(y, 10) : parseInt(y, 10);
  const dt = new Date(Date.UTC(y, mo - 1, d));
  if (dt.getUTCFullYear() !== y || dt.getUTCMonth() !== mo - 1 || dt.getUTCDate() !== d) return null;
  return dt.toISOString().slice(0, 10);
}

function DmyInput({ value, onChange, testId }) {
  const [text, setText] = useState(ymdToDmy(value));
  useEffect(() => { setText(ymdToDmy(value)); }, [value]);
  const commit = () => {
    if (!text.trim()) { onChange(""); return; }
    const ymd = dmyToYmd(text);
    if (!ymd) { toast.error("Use dd/mm/yy"); setText(ymdToDmy(value)); return; }
    onChange(ymd);
  };
  return (
    <Input value={text} onChange={(e) => setText(e.target.value)} onBlur={commit}
      onKeyDown={(e) => e.key === "Enter" && commit()} placeholder="dd/mm/yy" className="w-[110px]" data-testid={testId} />
  );
}

export default function MarketingSpecials() {
  const { bid } = useParams();
  const { staff } = useAuth();
  const canCreate = hasPermission(staff, "location.specials.create");
  const canEdit = hasPermission(staff, "location.specials.edit");
  const canDelete = hasPermission(staff, "location.specials.delete");
  const [branch, setBranch] = useState(null);
  const [items, setItems] = useState([]);
  const [catalog, setCatalog] = useState({ services: [], products: [], packages: [], gifts: [], series: [] });
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(empty);
  const [usage, setUsage] = useState({});
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const load = useCallback(async () => {
    try {
      const [b, s, sv, pr, pk, gt, sr, us] = await Promise.all([
        staffApi.get("/branches"),
        staffApi.get(`/branches/${bid}/specials`),
        staffApi.get("/services", { params: { branch_id: bid } }).catch(() => ({ data: [] })),
        staffApi.get("/products").catch(() => ({ data: [] })),
        staffApi.get("/packages", { params: { branch_id: bid } }).catch(() => ({ data: [] })),
        staffApi.get(`/branches/${bid}/gift-card-templates`).catch(() => ({ data: [] })),
        staffApi.get("/series-templates").catch(() => ({ data: [] })),
        staffApi.get(`/branches/${bid}/specials-usage`).catch(() => ({ data: {} })),
      ]);
      setBranch(b.data.find((x) => x.id === bid) || null);
      setItems(s.data);
      setUsage(us.data || {});
      setCatalog({ services: sv.data || [], products: pr.data || [], packages: pk.data || [], gifts: gt.data || [], series: sr.data || [] });
    } catch (e) { toast.error(formatApiError(e)); }
  }, [bid]);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm(empty); setStep(1); setOpen(true); };
  const openEdit = (s) => { setEditing(s); setForm({ ...empty, ...s }); setStep(1); setOpen(true); };
  const openCopy = (s) => {
    const { id, ...rest } = s;
    setEditing(null);
    setForm({ ...empty, ...rest, name: `${s.name} (copy)` });
    setStep(1); setOpen(true);
  };

  // ---- Search criteria (client-side) ----
  const [q, setQ] = useState({ name: "", code: "", type: "any" });
  const [applied, setApplied] = useState({ name: "", code: "", type: "any" });
  const filtered = useMemo(() => items.filter((s) => {
    if (applied.name && !(s.name || "").toLowerCase().includes(applied.name.toLowerCase())) return false;
    if (applied.code && !(s.promo_code || "").toLowerCase().includes(applied.code.toLowerCase())) return false;
    if (applied.type !== "any" && (s.special_type || "code") !== applied.type) return false;
    return true;
  }), [items, applied]);
  const totals = useMemo(() => filtered.reduce((acc, s) => {
    const u = usage[s.id] || {};
    acc.used += u.used || 0; acc.revenue += u.revenue || 0;
    return acc;
  }, { used: 0, revenue: 0 }), [filtered, usage]);

  const validateStep = () => {
    if (step === 1) {
      if (!form.name.trim()) return "Name is required";
      if (form.special_type === "code" && !form.promo_code.trim()) return "A code is required for this type";
    }
    if (step === 2 && form.starts_on && form.ends_on && form.ends_on < form.starts_on) return "'Until' must be after 'From'";
    if (step === 3 && form.time_mode === "specific" && (!form.time_start || !form.time_end)) return "Pick the time window";
    if (step === 4 && form.day_mode === "custom" && form.days.length === 0) return "Tick at least one day";
    if (step === 5 && form.items_mode === "custom" && form.item_ids.length === 0) return "Tick at least one item";
    if (step === 6) {
      if (form.pricing_mode === "discount" && !(Number(form.discount_value) > 0)) return "Enter the discount value";
      if (form.pricing_mode === "price_ranges" && form.price_ranges.length === 0) return "Add at least one price range";
      if (form.pricing_mode === "free_addon" && !form.addon_ref_id) return "Choose the free add-on item";
    }
    return null;
  };
  const next = () => { const err = validateStep(); if (err) return toast.error(err); setStep((s) => s + 1); };

  const save = async () => {
    const err = validateStep();
    if (err) return toast.error(err);
    try {
      const payload = { ...form, discount_value: Number(form.discount_value) || 0 };
      if (editing) await staffApi.put(`/specials/${editing.id}`, payload);
      else await staffApi.post(`/branches/${bid}/specials`, payload);
      toast.success(`Special "${form.name}" saved`);
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (s) => {
    if (!window.confirm(`Delete "${s.name}"?`)) return;
    try { await staffApi.delete(`/specials/${s.id}`); load(); } catch (e) { toast.error(formatApiError(e)); }
  };
  const toggle = async (s) => {
    try { await staffApi.put(`/specials/${s.id}`, { ...empty, ...s, active: !s.active }); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const pricingLabel = (s) => {
    const mode = s.pricing_mode || "discount";
    if (mode === "price_ranges") return `${(s.price_ranges || []).length} price range(s)`;
    if (mode === "free_addon") return "Free add-on";
    return s.discount_type === "percent" ? `${s.discount_value}% off` : `${formatIDR(s.discount_value)} off`;
  };

  return (
    <StaffShell context="branch" branchName={branch?.name}>
      <div className="max-w-6xl">
        <p className="tracking-overline text-xs text-muted-foreground">MARKETING</p>
        <div className="flex items-end justify-between mb-6">
          <div>
            <h1 className="text-3xl font-semibold mt-1 mb-1" data-testid="specials-title">Specials</h1>
            <p className="text-sm text-muted-foreground">Promos validated against the client&apos;s visit date, time, day and eligible items. One special per line at checkout.</p>
          </div>
          <Button onClick={openNew} data-testid="new-special-btn" className={"gap-1 " + (canCreate ? "" : "hidden")}><Plus className="w-4 h-4" /> New Special</Button>
        </div>

        {/* ------ Search criteria (like the legacy Specials screen) ------ */}
        <Card className="rounded-xl mb-5">
          <CardContent className="p-4">
            <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-3">Search Criteria</p>
            <div className="flex flex-wrap items-end gap-3">
              <div><Label className="text-xs">Name</Label><Input value={q.name} onChange={(e) => setQ({ ...q, name: e.target.value })} className="w-[200px] h-9" data-testid="sp-search-name" /></div>
              <div><Label className="text-xs">Code</Label><Input value={q.code} onChange={(e) => setQ({ ...q, code: e.target.value })} className="w-[160px] h-9" data-testid="sp-search-code" /></div>
              <div>
                <Label className="text-xs">Type</Label>
                <Select value={q.type} onValueChange={(v) => setQ({ ...q, type: v })}>
                  <SelectTrigger className="w-[220px] h-9" data-testid="sp-search-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Any</SelectItem>
                    <SelectItem value="auto_all">Auto apply to all customers</SelectItem>
                    <SelectItem value="auto_members">Auto apply to all members</SelectItem>
                    <SelectItem value="code">Code required manually</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2 ml-auto">
                <Button variant="outline" size="sm" onClick={() => { setQ({ name: "", code: "", type: "any" }); setApplied({ name: "", code: "", type: "any" }); }} data-testid="sp-search-reset">Reset</Button>
                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setApplied(q)} data-testid="sp-search-btn">Search</Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <p className="text-xs text-muted-foreground mb-2" data-testid="sp-results-count">Results 1–{filtered.length} of {filtered.length}</p>
        <Card className="rounded-xl">
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-muted/60 text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="px-3 py-2">Name</th>
                  <th className="px-3 py-2">Code</th>
                  <th className="px-3 py-2">Type</th>
                  <th className="px-3 py-2">Applicable Dates</th>
                  <th className="px-3 py-2 text-right">Used</th>
                  <th className="px-3 py-2 text-right">Revenue</th>
                  <th className="px-3 py-2">Adjustment</th>
                  <th className="px-3 py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr><td colSpan={8} className="px-3 py-8 text-center text-muted-foreground">No specials match.</td></tr>
                )}
                {filtered.map((s) => {
                  const u = usage[s.id] || {};
                  const inactive = s.active === false;
                  return (
                    <tr key={s.id} className={"border-t " + (inactive ? "bg-rose-50/70 text-muted-foreground" : "hover:bg-muted/30")} data-testid={`special-row-${s.id}`}>
                      <td className="px-3 py-2">
                        <div className="font-medium text-foreground flex items-center gap-1.5">
                          <Tag className="w-3.5 h-3.5 text-primary shrink-0" />{s.name}
                          {s.internal_only && <Badge variant="outline" className="rounded-full text-[9px] bg-amber-50 text-amber-800 border-amber-200">Internal</Badge>}
                        </div>
                        {s.description && <div className="text-[11px] text-muted-foreground line-clamp-1">{s.description}</div>}
                      </td>
                      <td className="px-3 py-2 font-mono text-xs">{s.promo_code || "—"}</td>
                      <td className="px-3 py-2 text-xs">{TYPE_LABELS[s.special_type] || "Code required manually"}</td>
                      <td className="px-3 py-2 text-xs whitespace-nowrap">
                        {(s.starts_on || s.ends_on) ? `${s.starts_on ? ymdToDmy(s.starts_on) : "…"} – ${s.ends_on ? ymdToDmy(s.ends_on) : "…"}` : "Always"}
                        {s.time_mode === "specific" && <div className="text-[10px]">{s.time_start}–{s.time_end}</div>}
                        {s.day_mode === "custom" && <div className="text-[10px] capitalize">{(s.days || []).join(", ")}</div>}
                      </td>
                      <td className="px-3 py-2 text-right font-medium" data-testid={`special-used-${s.id}`}>{u.used || 0}</td>
                      <td className="px-3 py-2 text-right" data-testid={`special-revenue-${s.id}`}>{formatIDR(u.revenue || 0)}</td>
                      <td className="px-3 py-2 text-xs whitespace-nowrap">{pricingLabel(s)}</td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <div className="flex items-center gap-2 text-xs">
                          {canEdit && (
                            <button className="text-sky-700 hover:underline" onClick={() => openEdit(s)} data-testid={`special-edit-${s.id}`}>Edit</button>
                          )}
                          {canCreate && (
                            <button className="text-sky-700 hover:underline" onClick={() => openCopy(s)} data-testid={`special-copy-${s.id}`}>Copy</button>
                          )}
                          {canDelete && (
                            <button className="text-rose-700 hover:underline" onClick={() => remove(s)} data-testid={`special-delete-${s.id}`}>Delete</button>
                          )}
                          {canEdit && (
                            <Switch checked={s.active !== false} onCheckedChange={() => toggle(s)} className="scale-75" data-testid={`special-active-${s.id}`} />
                          )}
                          {!canEdit && !canCreate && !canDelete && (
                            <span className="text-muted-foreground italic">No actions</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              {filtered.length > 0 && (
                <tfoot>
                  <tr className="border-t bg-muted/40 font-semibold">
                    <td className="px-3 py-2 text-xs uppercase tracking-wider" colSpan={4}>Totals (paid invoices)</td>
                    <td className="px-3 py-2 text-right" data-testid="sp-total-used">{totals.used}</td>
                    <td className="px-3 py-2 text-right" data-testid="sp-total-revenue">{formatIDR(totals.revenue)}</td>
                    <td colSpan={2}></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </CardContent>
        </Card>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="special-wizard" aria-describedby="special-wizard-desc">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Special" : "New Special"} — Step {step} of 6: {STEP_TITLES[step - 1]}</DialogTitle>
            <DialogDescription id="special-wizard-desc" className="text-xs">Six quick steps to define who gets the promo, when it is valid and what it does to the price.</DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-1 mb-2">
            {STEP_TITLES.map((t, i) => (
              <button key={t} onClick={() => i + 1 < step && setStep(i + 1)}
                className={"h-1.5 flex-1 rounded-full transition-colors " + (i + 1 <= step ? "bg-emerald-600" : "bg-neutral-200")}
                data-testid={`wizard-step-dot-${i + 1}`} />
            ))}
          </div>

          {step === 1 && (
            <div className="space-y-3">
              <div><Label>Name *</Label><Input value={form.name} onChange={(e) => set({ name: e.target.value })} data-testid="sp-name" /></div>
              <div><Label>Description</Label><Textarea rows={2} value={form.description} onChange={(e) => set({ description: e.target.value })} data-testid="sp-description" /></div>
              <div>
                <Label>Type</Label>
                <Select value={form.special_type} onValueChange={(v) => set({ special_type: v })}>
                  <SelectTrigger data-testid="sp-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto_all">Auto apply to all customers</SelectItem>
                    <SelectItem value="auto_members">Auto apply to all members</SelectItem>
                    <SelectItem value="code">Code required manually</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-muted-foreground mt-1">
                  {form.special_type === "auto_all" && "Applies automatically to every eligible line on any customer's bill."}
                  {form.special_type === "auto_members" && "Applies automatically, but only for customers with an active membership."}
                  {form.special_type === "code" && "Staff must enter the code below when applying it to a line."}
                </p>
              </div>
              {form.special_type === "code" && (
                <div><Label>Code *</Label><Input value={form.promo_code} onChange={(e) => set({ promo_code: e.target.value.toUpperCase() })} className="max-w-xs font-mono" data-testid="sp-code" /></div>
              )}
              <label className="flex items-center gap-2 text-sm">
                <Checkbox checked={form.internal_only} onCheckedChange={(v) => set({ internal_only: !!v })} data-testid="sp-internal-only" />
                Internal use only — only employees can assign this special to a client invoice
              </label>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">Validity window judged against the date the client visits (their appointment date). Leave blank for no limit.</p>
              <div className="flex items-center gap-4">
                <div><Label>From</Label><DmyInput value={form.starts_on} onChange={(v) => set({ starts_on: v })} testId="sp-from" /></div>
                <div><Label>Until</Label><DmyInput value={form.ends_on} onChange={(v) => set({ ends_on: v })} testId="sp-until" /></div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-3">
              <label className="flex items-center gap-2 text-sm"><input type="radio" checked={form.time_mode === "flexible"} onChange={() => set({ time_mode: "flexible" })} data-testid="sp-time-flexible" /> Flexible time — redeemable any time of day</label>
              <label className="flex items-center gap-2 text-sm"><input type="radio" checked={form.time_mode === "specific"} onChange={() => set({ time_mode: "specific" })} data-testid="sp-time-specific" /> Specific time (happy hour)</label>
              {form.time_mode === "specific" && (
                <div className="flex items-center gap-3 pl-6">
                  <Input type="time" value={form.time_start} onChange={(e) => set({ time_start: e.target.value })} className="w-[120px]" data-testid="sp-time-start" />
                  <span className="text-sm text-muted-foreground">to</span>
                  <Input type="time" value={form.time_end} onChange={(e) => set({ time_end: e.target.value })} className="w-[120px]" data-testid="sp-time-end" />
                </div>
              )}
            </div>
          )}

          {step === 4 && (
            <div className="space-y-3">
              <label className="flex items-center gap-2 text-sm"><input type="radio" checked={form.day_mode === "anyday"} onChange={() => set({ day_mode: "anyday" })} data-testid="sp-day-anyday" /> Any day</label>
              <label className="flex items-center gap-2 text-sm"><input type="radio" checked={form.day_mode === "custom"} onChange={() => set({ day_mode: "custom" })} data-testid="sp-day-custom" /> Custom days (Monday promo / Weekend promo)</label>
              {form.day_mode === "custom" && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pl-6">
                  {WEEK.map((d) => (
                    <label key={d.key} className="flex items-center gap-2 text-sm">
                      <Checkbox checked={form.days.includes(d.key)}
                        onCheckedChange={(v) => set({ days: v ? [...form.days, d.key] : form.days.filter((x) => x !== d.key) })}
                        data-testid={`sp-day-${d.key}`} />
                      {d.label}
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}

          {step === 5 && <ItemsStep form={form} set={set} catalog={catalog} />}

          {step === 6 && <PricingStep form={form} set={set} catalog={catalog} />}

          <DialogFooter className="flex items-center sm:justify-between gap-2 pt-2">
            <Button variant="ghost" onClick={() => (step > 1 ? setStep(step - 1) : setOpen(false))} data-testid="wizard-back">{step > 1 ? "Back" : "Cancel"}</Button>
            {step < 6
              ? <Button onClick={next} data-testid="wizard-next">Next</Button>
              : <Button onClick={save} className="bg-emerald-600 hover:bg-emerald-700" data-testid="wizard-finish">Finish & Save</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </StaffShell>
  );
}

/* Step 5 — collapsible category sections with all/none, like the staff
   services setup, spanning every sellable kind. */
function ItemsStep({ form, set, catalog }) {
  const sections = useMemo(() => {
    const byCat = (list) => {
      const m = {};
      for (const it of list) (m[it.category || "Other"] = m[it.category || "Other"] || []).push(it);
      return Object.entries(m).sort(([a], [b]) => a.localeCompare(b)).map(([name, its]) => ({ name, items: its }));
    };
    return [
      { title: "Services", groups: byCat(catalog.services) },
      { title: "Products", groups: byCat(catalog.products) },
      { title: "Packages", groups: [{ name: "All packages", items: catalog.packages }] },
      { title: "Gift Cards", groups: [{ name: "Templates", items: catalog.gifts.map((g) => ({ ...g, name: g.title })) }] },
      { title: "Series", groups: [{ name: "Templates", items: catalog.series }] },
    ];
  }, [catalog]);

  const toggleIds = (ids, on) => {
    const cur = new Set(form.item_ids);
    ids.forEach((id) => (on ? cur.add(id) : cur.delete(id)));
    set({ item_ids: [...cur] });
  };

  return (
    <div className="space-y-3">
      <label className="flex items-center gap-2 text-sm font-medium">
        <Checkbox checked={form.items_mode === "all"} onCheckedChange={(v) => set({ items_mode: v ? "all" : "custom" })} data-testid="sp-items-all" />
        All treatments, products, packages, gift cards and series
      </label>
      {form.items_mode === "custom" && sections.map((sec) => (
        <div key={sec.title} className="border rounded-md">
          <div className="px-3 py-2 bg-muted/50 text-sm font-semibold">{sec.title}</div>
          {sec.groups.map((g) => (
            <CollapsibleGroup key={`${sec.title}-${g.name}`} section={sec.title} group={g} selected={form.item_ids} toggleIds={toggleIds} />
          ))}
        </div>
      ))}
      {form.items_mode === "custom" && <p className="text-xs text-muted-foreground">{form.item_ids.length} item(s) selected</p>}
    </div>
  );
}

function CollapsibleGroup({ section, group, selected, toggleIds }) {
  const [openG, setOpenG] = useState(false);
  const ids = group.items.map((i) => i.id);
  const picked = ids.filter((id) => selected.includes(id)).length;
  const slug = `${section}-${group.name}`.toLowerCase().replace(/[^a-z0-9]+/g, "-");
  return (
    <div className="border-t">
      <div className="flex items-center justify-between px-3 py-1.5">
        <button className="flex items-center gap-1.5 text-sm" onClick={() => setOpenG(!openG)} data-testid={`sp-group-${slug}`}>
          {openG ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
          {group.name} <span className="text-xs text-muted-foreground">({picked}/{ids.length})</span>
        </button>
        <div className="flex gap-2 text-xs">
          <button className="text-emerald-700 hover:underline" onClick={() => toggleIds(ids, true)} data-testid={`sp-group-all-${slug}`}>All</button>
          <button className="text-rose-700 hover:underline" onClick={() => toggleIds(ids, false)} data-testid={`sp-group-none-${slug}`}>None</button>
        </div>
      </div>
      {openG && (
        <div className="px-3 pb-2 grid grid-cols-1 sm:grid-cols-2 gap-1">
          {group.items.map((it) => (
            <label key={it.id} className="flex items-center gap-2 text-xs">
              <Checkbox checked={selected.includes(it.id)} onCheckedChange={(v) => toggleIds([it.id], !!v)} data-testid={`sp-item-${it.id}`} />
              <span className="truncate">{it.name}{it.price ? ` (${formatIDR(it.price)})` : ""}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

/* Step 6 — pricing information: discount / price ranges / free add-on. */
function PricingStep({ form, set, catalog }) {
  const addonList = form.addon_kind === "service" ? catalog.services : catalog.products;
  const addRange = () => set({ price_ranges: [...form.price_ranges, { min: 0, max: 0, discount_type: "percent", discount_value: 10 }] });
  const setRange = (i, patch) => set({ price_ranges: form.price_ranges.map((r, j) => (j === i ? { ...r, ...patch } : r)) });
  return (
    <div className="space-y-4">
      <label className="flex items-center gap-2 text-sm font-medium">
        <input type="radio" checked={form.pricing_mode === "discount"} onChange={() => set({ pricing_mode: "discount" })} data-testid="sp-pricing-discount" />
        Apply a Discount to applicable items
      </label>
      {form.pricing_mode === "discount" && (
        <div className="pl-6 space-y-2">
          <label className="flex items-center gap-2 text-sm"><input type="radio" checked={form.discount_type === "fixed"} onChange={() => set({ discount_type: "fixed" })} data-testid="sp-disc-amount" /> Amount (Rp)</label>
          <label className="flex items-center gap-2 text-sm"><input type="radio" checked={form.discount_type === "percent"} onChange={() => set({ discount_type: "percent" })} data-testid="sp-disc-percent" /> Percentage (%)</label>
          <Input type="number" min={0} value={form.discount_value} onChange={(e) => set({ discount_value: e.target.value })} className="w-[140px]" data-testid="sp-disc-value" />
        </div>
      )}
      <label className="flex items-center gap-2 text-sm font-medium">
        <input type="radio" checked={form.pricing_mode === "price_ranges"} onChange={() => set({ pricing_mode: "price_ranges" })} data-testid="sp-pricing-ranges" />
        Apply a Discount based on price ranges
      </label>
      {form.pricing_mode === "price_ranges" && (
        <div className="pl-6 space-y-2">
          {form.price_ranges.map((r, i) => (
            <div key={i} className="flex items-center gap-2 text-sm flex-wrap" data-testid={`sp-range-${i}`}>
              <Input type="number" placeholder="Min Rp" value={r.min} onChange={(e) => setRange(i, { min: Number(e.target.value) })} className="w-[110px]" data-testid={`sp-range-min-${i}`} />
              <span>–</span>
              <Input type="number" placeholder="Max Rp (0 = ∞)" value={r.max} onChange={(e) => setRange(i, { max: Number(e.target.value) })} className="w-[130px]" data-testid={`sp-range-max-${i}`} />
              <Select value={r.discount_type} onValueChange={(v) => setRange(i, { discount_type: v })}>
                <SelectTrigger className="w-[110px] h-9" data-testid={`sp-range-type-${i}`}><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="percent">%</SelectItem><SelectItem value="fixed">Rp</SelectItem></SelectContent>
              </Select>
              <Input type="number" value={r.discount_value} onChange={(e) => setRange(i, { discount_value: Number(e.target.value) })} className="w-[100px]" data-testid={`sp-range-value-${i}`} />
              <Button size="icon" variant="ghost" className="h-7 w-7 text-rose-600" onClick={() => set({ price_ranges: form.price_ranges.filter((_, j) => j !== i) })} data-testid={`sp-range-delete-${i}`}><Trash2 className="w-3.5 h-3.5" /></Button>
            </div>
          ))}
          <Button size="sm" variant="outline" onClick={addRange} data-testid="sp-add-range"><Plus className="w-3.5 h-3.5 mr-1" /> Add range</Button>
        </div>
      )}
      <label className="flex items-center gap-2 text-sm font-medium">
        <input type="radio" checked={form.pricing_mode === "free_addon"} onChange={() => set({ pricing_mode: "free_addon" })} data-testid="sp-pricing-addon" />
        Select free Add-on (item automatically added to the order)
      </label>
      {form.pricing_mode === "free_addon" && (
        <div className="pl-6 flex items-center gap-2">
          <Select value={form.addon_kind} onValueChange={(v) => set({ addon_kind: v, addon_ref_id: "" })}>
            <SelectTrigger className="w-[130px]" data-testid="sp-addon-kind"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="product">Product</SelectItem><SelectItem value="service">Service</SelectItem></SelectContent>
          </Select>
          <Select value={form.addon_ref_id} onValueChange={(v) => set({ addon_ref_id: v })}>
            <SelectTrigger className="w-[280px]" data-testid="sp-addon-item"><SelectValue placeholder="Choose the free item" /></SelectTrigger>
            <SelectContent>
              {addonList.map((it) => <SelectItem key={it.id} value={it.id}>{it.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}
    </div>
  );
}
